import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSeo from '../lib/seo';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowLeft } from 'lucide-react';
import { portalToElement } from '../components/site/Portal';
import createShortcutPT from '../assets/meu-atalho/1-PT.webp';
import createShortcutEN from '../assets/meu-atalho/1-EN.webp';
import homePT from '../assets/meu-atalho/2-PT.webp';
import homeEN from '../assets/meu-atalho/2-EN.webp';
import categoriesPT from '../assets/meu-atalho/3-PT.webp';
import categoriesEN from '../assets/meu-atalho/3-EN.webp';
import keyboardPT from '../assets/meu-atalho/4-PT.webp';
import keyboardEN from '../assets/meu-atalho/4-EN.webp';

gsap.registerPlugin(ScrollTrigger, useGSAP);

function getPreferredLanguage() {
  if (typeof navigator === 'undefined') return 'en';

  const languages = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language, navigator.userLanguage];

  const normalizedLanguages = languages
    .filter(Boolean)
    .map((language) => language.toLowerCase());

  for (const language of normalizedLanguages) {
    if (language === 'pt' || language.startsWith('pt-')) return 'pt';
    if (language === 'es' || language.startsWith('es-')) return 'es';
    if (language === 'en' || language.startsWith('en-')) return 'en';
  }

  return 'en';
}

const copy = {
  pt: {
    back: 'Voltar',
    eyebrow: 'App de produtividade',
    title: 'Meu Atalho',
    subtitle:
      'Salve textos importantes, organize por categoria e copie tudo com um toque.',
    cta: 'Ver recursos',
    stats: [
      ['Textos', 'sempre a mao'],
      ['Categorias', 'para organizar melhor'],
      ['1 toque', 'para copiar atalhos'],
    ],
    sectionsTitle: 'Fluxos do app',
    sectionsSubtitle:
      'Da criacao ao uso no teclado, cada tela foi pensada para reduzir repeticao e manter os atalhos sempre a mao.',
    highlights: [
      'Cadastro rapido de atalhos com titulo, conteudo e categoria.',
      'Home focada em busca, copia e acesso imediato aos textos salvos.',
      'Categorias para separar mensagens por contexto de uso.',
      'Teclado personalizado para inserir atalhos sem sair da conversa.',
    ],
    screens: [
      {
        title: 'Criar atalho',
        text: 'Cadastre frases frequentes, nomes e modelos de resposta com campos diretos e faceis de revisar.',
      },
      {
        title: 'Home de atalhos',
        text: 'Visualize os atalhos salvos, copie o texto certo e mantenha suas respostas mais usadas por perto.',
      },
      {
        title: 'Categorias',
        text: 'Agrupe conteudos por assunto para encontrar rapidamente o que combina com cada momento.',
      },
      {
        title: 'Teclado personalizado',
        text: 'Use seus atalhos direto do teclado, sem alternar entre apps quando estiver escrevendo.',
      },
    ],
  },
  es: {
    back: 'Volver',
    eyebrow: 'App de productividad',
    title: 'Mi atajo',
    subtitle:
      'Guarda textos importantes, organizalos por categoria y copialos con un toque.',
    cta: 'Ver funciones',
    stats: [
      ['Textos', 'siempre a mano'],
      ['Categorias', 'para organizar mejor'],
      ['1 toque', 'para copiar atajos'],
    ],
    sectionsTitle: 'Flujos de la app',
    sectionsSubtitle:
      'Desde la creacion hasta el uso en el teclado, cada pantalla fue pensada para reducir repeticiones y mantener tus atajos cerca.',
    highlights: [
      'Creacion rapida de atajos con titulo, contenido y categoria.',
      'Pantalla inicial enfocada en busqueda, copia y acceso inmediato.',
      'Categorias para separar mensajes por contexto de uso.',
      'Teclado personalizado para insertar atajos sin salir de la conversacion.',
    ],
    screens: [
      {
        title: 'Crear atajo',
        text: 'Guarda frases frecuentes, nombres y modelos de respuesta con campos directos y faciles de revisar.',
      },
      {
        title: 'Inicio de atajos',
        text: 'Consulta los atajos guardados, copia el texto correcto y manten cerca tus respuestas mas usadas.',
      },
      {
        title: 'Categorias',
        text: 'Agrupa contenidos por tema para encontrar rapidamente lo que corresponde a cada momento.',
      },
      {
        title: 'Teclado personalizado',
        text: 'Usa tus atajos directamente desde el teclado sin cambiar de app mientras escribes.',
      },
    ],
  },
  en: {
    back: 'Back',
    eyebrow: 'Productivity app',
    title: 'Quick Shortcut',
    subtitle:
      'Save important texts, organize them by category, and copy everything with one tap.',
    cta: 'See features',
    stats: [
      ['Texts', 'always close'],
      ['Categories', 'to stay organized'],
      ['1 tap', 'to copy shortcuts'],
    ],
    sectionsTitle: 'App flows',
    sectionsSubtitle:
      'From creation to keyboard use, each screen is designed to reduce repetition and keep shortcuts close.',
    highlights: [
      'Fast shortcut creation with title, content, and category.',
      'A focused home screen for search, copy, and quick access.',
      'Categories to separate messages by usage context.',
      'A custom keyboard for inserting shortcuts without leaving the conversation.',
    ],
    screens: [
      {
        title: 'Create shortcut',
        text: 'Save frequent phrases, names, and reply templates with direct fields that are easy to review.',
      },
      {
        title: 'Shortcut home',
        text: 'Browse saved shortcuts, copy the right text, and keep your most-used replies close.',
      },
      {
        title: 'Categories',
        text: 'Group content by topic so each shortcut is easy to find at the right moment.',
      },
      {
        title: 'Custom keyboard',
        text: 'Use your shortcuts directly from the keyboard without switching apps while writing.',
      },
    ],
  },
};

function Phone({ src, keyboard = false, className = '' }) {
  return (
    <div className={`hv3-phone ${className}`}>
      <div className="hv3-phone-screen">
        <img src={src} alt="" width="720" height="1600" decoding="async" style={keyboard ? { objectPosition: 'center bottom' } : undefined} />
      </div>
      <span className="hv3-phone-island" />
    </div>
  );
}

const AUTOPLAY_MS = 5200;

function MeuAtalho() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const language = useMemo(getPreferredLanguage, []);
  const text = copy[language];
  const root = useRef(null);
  const shotRef = useRef(null);

  const screens = useMemo(() => {
    const images = language === 'pt'
      ? [createShortcutPT, homePT, categoriesPT, keyboardPT]
      : [createShortcutEN, homeEN, categoriesEN, keyboardEN];

    return text.screens.map((screen, index) => ({
      ...screen,
      image: images[index],
    }));
  }, [language, text.screens]);

  useSeo({ title: `${text.title} - Idealizer`, description: text.subtitle, path: '/apps/meu-atalho' });

  const activeScreen = screens[currentIndex];

  // Entrada do topo + parallax dos celulares e da galeria.
  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap
          .timeline({ delay: 0.15 })
          .fromTo(q('.pg-rise'), { y: 0, yPercent: 115 }, { yPercent: 0, duration: 1.2, ease: 'expo.out' })
          .fromTo(q('.pg-fade'), { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 1, ease: 'expo.out', stagger: 0.07 }, 0.3)
          .fromTo(q('.ma-phone--front'), { yPercent: 24, rotate: 10, autoAlpha: 0 }, { yPercent: 0, rotate: -5, autoAlpha: 1, duration: 1.4, ease: 'expo.out' }, 0.2)
          .fromTo(q('.ma-phone--back'), { yPercent: 30, rotate: -2, autoAlpha: 0 }, { yPercent: 0, rotate: 7, autoAlpha: 1, duration: 1.4, ease: 'expo.out' }, 0.32);
        gsap.to(q('.ma-phone--back'), {
          yPercent: -14,
          ease: 'none',
          scrollTrigger: { trigger: q('.ma-hero')[0], start: 'top top', end: 'bottom top', scrub: true },
        });
        gsap.from(q('.ma-card'), {
          y: 60,
          autoAlpha: 0,
          duration: 1.1,
          ease: 'expo.out',
          stagger: 0.08,
          scrollTrigger: { trigger: q('.ma-gallery')[0], start: 'top 85%' },
        });
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  // Troca de tela no destaque: a nova entra com leve blur e escala.
  useEffect(() => {
    const el = shotRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    gsap.fromTo(el, { autoAlpha: 0, scale: 0.96, filter: 'blur(8px)' }, { autoAlpha: 1, scale: 1, filter: 'blur(0px)', duration: 0.6, ease: 'expo.out', clearProps: 'filter' });
  }, [currentIndex]);

  // Avança sozinho pelos fluxos (a barra de progresso mostra o tempo); pausa com o mouse ou o foco em cima.
  useEffect(() => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setTimeout(() => setCurrentIndex((i) => (i + 1) % screens.length), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [currentIndex, paused, screens.length]);

  const toFlows = (e) => {
    e.preventDefault();
    portalToElement('#meu-atalho-flows', e);
  };

  return (
    <main className="pg ma-v3" ref={root}>
      <section className="container ma-hero">
        <div className="ma-copy">
          <button type="button" className="pg-back pg-fade" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} strokeWidth={2} aria-hidden /> {text.back}
          </button>
          <span className="ma-eyebrow pg-fade">{text.eyebrow}</span>
          <h1 className="pg-title ma-title">
            <span className="pg-line">
              <span className="pg-rise">{text.title}</span>
            </span>
          </h1>
          <p className="pg-lede pg-fade">{text.subtitle}</p>
          <div className="pg-actions pg-fade">
            <a href="#meu-atalho-flows" className="btn-primary" onClick={toFlows}>
              {text.cta}
            </a>
          </div>
          <dl className="ma-stats pg-fade" aria-label={`${text.title} highlights`}>
            {text.stats.map(([value, label]) => (
              <div className="ma-stat" key={label}>
                <dt>{value}</dt>
                <dd>{label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="ma-stage" aria-hidden="true">
          <div className="ma-glow" />
          <Phone src={screens[1].image} className="ma-phone ma-phone--back" />
          <Phone src={screens[0].image} className="ma-phone ma-phone--front" />
        </div>
      </section>

      <section className="container ma-flows" id="meu-atalho-flows">
        <div className="ma-head">
          <h2 className="ma-h2">{text.sectionsTitle}</h2>
          <p>{text.sectionsSubtitle}</p>
        </div>

        <div
          className="ma-flow-grid"
          onPointerEnter={() => setPaused(true)}
          onPointerLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div className="ma-tabs" role="tablist" aria-label={text.sectionsTitle}>
            {text.highlights.map((highlight, index) => (
              <button
                type="button"
                role="tab"
                className={`ma-tab ${index === currentIndex ? 'is-active' : ''}`}
                key={highlight}
                onClick={() => setCurrentIndex(index)}
                aria-selected={index === currentIndex}
                aria-controls="ma-showcase"
              >
                <span className="ma-tab-title">{screens[index].title}</span>
                <span className="ma-tab-text">{highlight}</span>
                <span className="ma-tab-bar" aria-hidden>
                  <span
                    key={`${currentIndex}-${paused}`}
                    className={`ma-tab-fill ${index === currentIndex && !paused ? 'is-running' : ''}`}
                    style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                  />
                </span>
              </button>
            ))}
          </div>

          <article className="ma-showcase" id="ma-showcase" role="tabpanel" aria-live="polite">
            <div className="ma-showcase-glow" aria-hidden />
            <div ref={shotRef} className="ma-shot">
              <div className="hv3-phone ma-shot-phone">
                <div className="hv3-phone-screen">
                  <img
                    src={activeScreen.image}
                    alt={activeScreen.title}
                    style={currentIndex === 3 ? { objectPosition: 'center bottom' } : undefined}
                  />
                </div>
                <span className="hv3-phone-island" aria-hidden />
              </div>
            </div>
            <div className="ma-showcase-copy">
              <h3>{activeScreen.title}</h3>
              <p>{activeScreen.text}</p>
            </div>
          </article>
        </div>
      </section>

      <section className="ma-gallery" aria-label={text.sectionsTitle}>
        <div className="ma-gallery-track">
          {screens.map((screen, index) => (
            <article className="ma-card" key={screen.title}>
              <Phone src={screen.image} keyboard={index === 3} className="ma-card-phone" />
              <h3>{screen.title}</h3>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

export default MeuAtalho;
